import { type QueryResult, type MutationResult } from '@apollo/client';
import loadable from '@loadable/component';
import { type FunctionComponent, type HTMLAttributes, useEffect } from 'react';
import { type RequiredPick, type PartialPick } from '../../../../@types/build';
import {
	type GetProductConfigurationQuery,
	type GetProductConfigurationQueryVariables,
	type AddProductToCartMutation,
	type ProductOptionGroupFieldsFragment,
	type ProductOptionFieldsFragment
} from '../../__generated__/graphql-client-types';
import { ERROR_LOADING_PRODUCT_CONFIGURATION } from '../../constants/product';
import { formatPrice } from '../../helpers/general-helper/general-helper';
import { calculateTotalItems, updateSelections } from '../../helpers/product-helper/configuration/product-configuration.helper';
import { type GroupSelection, type OptionSelection, type Selection } from '../../types/product-option.types';
import { PlaceholderImage } from '../images/placeholder-image/placeholder-image.component';
import { ProductOptionGroupList } from './product-option-group/product-option-group.component';

const CartRecentlyUpdatedComponent = loadable(
	() =>
		import(
			/* webpackChunkName: "cartrecentlyupdated" */
			'../cart-components/cart-recently-updated/cart-recently-updated.component'
		),
	{
		resolveComponent: ({ CartRecentlyUpdatedPanel }) => CartRecentlyUpdatedPanel
	}
);

/**
 * @typedef Configuration
 */
type Configuration = {
	id: number;
	selection: Selection;
	changeSelection: (selection?: Selection) => void;
	configStatus: QueryResult<GetProductConfigurationQuery, GetProductConfigurationQueryVariables>;
	resetAddToCart: () => void;
	addToCartStatus?: MutationResult<AddProductToCartMutation>;
};

/**
 * Product Configuration Content Props
 */
export type ProductConfigurationContentProps = {
	required: ProductOptionGroupFieldsFragment[];
} & Omit<Configuration, 'configStatus'> &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Configuration Content Component
 */
export const ProductConfigurationContent: FunctionComponent<ProductConfigurationContentProps> = ({
	className = 'flex flex-column justify-between w-100 ma0',
	id,
	selection,
	changeSelection,
	required,
	resetAddToCart,
	addToCartStatus
}) => {
	const { data } = addToCartStatus || {};
	const newItemIds = data?.addProductToCart?.newItemIds;
	const totalItems = calculateTotalItems(selection);
	/**
	 * Updates Selection with a given option and optionally a group.
	 * If group selection is available, the group selection will be updated as well.
	 */
	const update = (
		option: ProductOptionFieldsFragment,
		optionSelection: OptionSelection,
		groupSelection?: RequiredPick<Partial<GroupSelection>, 'id' | 'valid'>
	) => changeSelection(updateSelections(id, selection, { option, optionSelection, groupSelection }));

	// Reset last add to cart result if variant selection changes.

	useEffect(() => resetAddToCart(), [selection.id]);

	const itemCountText = totalItems > 0 ? ` (${totalItems} item${totalItems > 1 ? 's' : ''}):` : ':';
	const formatLabel = `Subtotal${itemCountText}`;

	return (
		<div className={className}>
			{newItemIds && <CartRecentlyUpdatedComponent className="pb6" newItemIds={newItemIds} selection={selection} id={id} />}
			<ProductOptionGroupList list={required} selection={selection} update={update} changeSelection={changeSelection} />
			{selection.groups.length > 0 && (
				<section className={`flex flex-row items-center w-100 f4 mt3`} data-testid="product-total">
					<strong>{formatLabel}</strong>
					<span className="b ml2">{formatPrice(selection.total)}</span>
				</section>
			)}
		</div>
	);
};

/**
 * Product Configuration Placeholder
 */
export const ProductConfigurationPlaceholder: FunctionComponent = () => (
	<div className="mt3">
		<div className={'placeholder w-50 w-20-ns h1'} />
		<PlaceholderImage height={64} className="w-100 w-third-ns h3 mt3" />
	</div>
);

/**
 * @typedef ProductConfigurationProps
 */
export type ProductConfigurationProps = PartialPick<Configuration, 'selection' | 'addToCartStatus'> &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Configuration Component
 */
export const ProductConfiguration: FunctionComponent<ProductConfigurationProps> = ({ className, configStatus, selection, ...props }) => {
	const configuration = configStatus.data?.getProductConfiguration;
	return (
		<section className={className}>
			{configStatus.error ? (
				<span className="b f5">{ERROR_LOADING_PRODUCT_CONFIGURATION}</span>
			) : configuration && selection ? (
				<ProductConfigurationContent {...configuration} {...props} selection={selection} />
			) : (
				<ProductConfigurationPlaceholder />
			)}
		</section>
	);
};
